import config from '../../config';
import { Api, ApiConfig, SKIP_AUTH_HEADER, SKIP_WORKSPACE_ID_HEADER } from '../api';
import { getCurrentProductCode, withPluginProductHeaders, withProductHeaders } from '../productContext';

export interface CreateCheckoutSessionRequest {
  amount: number;
  currency: string;
  payload: {
    type: string;
    params: Record<string, any>;
  };
  successUrl: string;
  cancelUrl: string;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

// User Plan interfaces
export interface UserPlan {
  nextResetDate: string | null;
  planName: string;
  planId: string;
  endAt: string;
  startAt: string;
  status: string;
  planCode: string;
  billingCycle: 'monthly' | 'yearly';
}

// User Points interfaces
export interface UserPoints {
  user_id: string;
  total_points: number;
  current_period_points: number;
  used_points: number;
  balance_points: number;
  last_reset_time: string;
  reset_count: number;
  last_update_time: string;
  daily_free_points: number;
  daily_free_used: number;
  daily_free_balance: number;
  last_daily_reset: string;
  last_login_date: string;
  total_remaining: number;
}

// User Points History interfaces
export interface PointsHistoryItem {
  id: string;
  user_id: string;
  used_points: number;
  session_id: string;
  task_name: string;
  operation_type: string;
  response_status: string;
  execution_time_ms: number;
  ip_address: string;
  created_at: string;
}

export interface GetUserPointsHistoryParams {
  page: number;
  size: number;
}

export interface GetUserPointsHistoryResponse {
  pagination: {
    total: number;
    size: number;
    totalPages: number;
    hasPrevious: boolean;
    hasNext: boolean;
    page: number;
  };
  data: PointsHistoryItem[];
}

// Create Subscription interfaces
export interface CreateSubscriptionRequest {
  planId: string;
  successUrl: string;
  cancelUrl: string;
  allowTrial?: boolean;
}

// Create Subscription Portal interfaces
export interface CreateSubscriptionPortalRequest {
  returnUrl: string;
}

export interface CreateSubscriptionPortalResponse {
  url: string;
  paymentType: string;
}

// Invoice interfaces
export interface Invoice {
  id: string;
  workspaceSubscriptionId: string;
  stripeInvoiceId: string;
  amountCents: number;
  currency: string;
  status: string;
  hostedInvoiceUrl: string;
  pdfUrl: string;
  createdAt: string;
}

export interface GetInvoicesResponse {
  pagination: {
    total: number;
    size: number;
    totalPages: number;
    hasPrevious: boolean;
    hasNext: boolean;
    page: number;
  };
  invoices: Invoice[];
}

export interface CreditTopupSku {
  skuCode: string;
  displayName: string;
  amountCents: number;
  currency: string;
  creditsGranted: number;
  workUnitsGranted?: number;
  displayUnit?: string | null;
}

export interface CreditTopupEligibilityResponse {
  eligible: boolean;
  eligibilityStatus?: string | null;
  reasonCode?: string | null;
  ctaAction?: string | null;
}

export interface CreateCreditTopupCheckoutRequest {
  skuCode: string;
  idempotencyKey?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface CreateCreditTopupCheckoutResponse {
  orderId: string;
  sessionId: string;
  checkoutUrl?: string;
  paymentUrl?: string;
  creditsGranted?: number | null;
  workUnitsGranted?: number | null;
  displayUnit?: string | null;
}

export interface SyncCreditTopupCheckoutSessionRequest {
  sessionId: string;
}

export interface SyncCreditTopupCheckoutSessionResponse {
  orderId?: string | null;
  sessionId?: string | null;
  status: string;
  credited?: boolean;
  processing?: boolean;
  paymentStatus?: string | null;
  paymentIntentId?: string | null;
  creditsGranted?: number | null;
  workUnitsGranted?: number | null;
  displayUnit?: string | null;
}

export interface CreditBalancePart {
  windowId?: number | string | null;
  validFrom?: string | null;
  validTo?: string | null;
  status?: string | null;
  displayUnit?: string | null;
  displayBudgetMicrousd?: number;
  displayUsedMicrousd?: number;
  displayRemainingMicrousd?: number;
  workUnitsBudgetDisplayMicrousd?: number;
  workUnitsUsedDisplayMicrousd?: number;
  workUnitsRemainingDisplayMicrousd?: number;
  creditsBudget?: number;
  creditsUsed?: number;
  creditsRemaining?: number;
  workUnitsBudget?: number;
  workUnitsUsed?: number;
  workUnitsRemaining?: number;
}

export interface CreditPlanEntitlement {
  planId?: string | null;
  planCode?: string | null;
  planName?: string | null;
  provider?: string | null;
  subscriptionStatus?: string | null;
  billingCycle?: string | null;
  displayBudgetMicrousd?: number | string | null;
  workUnitsBudget?: number | string | null;
  work_units_budget?: number | string | null;
  workUnitsBudgetDisplayMicrousd?: number | string | null;
  work_units_budget_display_microusd?: number | string | null;
  workUnitsRemaining?: number | string | null;
  work_units_remaining?: number | string | null;
  workUnitsRemainingDisplayMicrousd?: number | string | null;
  work_units_remaining_display_microusd?: number | string | null;
  workUnitsUsed?: number | string | null;
  work_units_used?: number | string | null;
  workUnitsUsedDisplayMicrousd?: number | string | null;
  work_units_used_display_microusd?: number | string | null;
  shopifyPendingDailyCredits?: number | string | null;
  shopifyPendingMaxCredits?: number | string | null;
}

export interface CreditSummaryResponse {
  workspace_id?: string;
  displayUnit?: string | null;
  display_unit?: string | null;
  provider?: string | null;
  billingProvider?: string | null;
  billing_provider?: string | null;
  paymentProvider?: string | null;
  payment_provider?: string | null;
  windowId?: number | string | null;
  validFrom?: string | null;
  validTo?: string | null;
  resetAt?: string | null;
  status?: string | null;
  usageStatus?: string | null;
  usage_status?: string | null;
  eligibilityStatus?: string | null;
  eligibility_status?: string | null;
  reasonCode?: string | null;
  reason_code?: string | null;
  paymentStatus?: string | null;
  payment_status?: string | null;
  shopifyPaymentStatus?: string | null;
  shopify_payment_status?: string | null;
  paymentPending?: boolean | null;
  payment_pending?: boolean | null;
  isPaymentPending?: boolean | null;
  is_payment_pending?: boolean | null;
  shopifyPaymentPending?: boolean | null;
  shopify_payment_pending?: boolean | null;
  planCreditRefreshCadence?: string | null;
  plan_credit_refresh_cadence?: string | null;
  planCreditsCadence?: string | null;
  plan_credits_cadence?: string | null;
  refreshCadence?: string | null;
  refresh_cadence?: string | null;
  ctaAction?: string | null;
  cta_action?: string | null;
  extraCreditsState?: string | null;
  extra_credits_state?: string | null;
  canBuyTopup?: boolean;
  can_buy_topup?: boolean;
  pendingDailyCredits?: number | string | null;
  pending_daily_credits?: number | string | null;
  dailyPendingCredits?: number | string | null;
  daily_pending_credits?: number | string | null;
  dailyAllowanceCredits?: number | string | null;
  daily_allowance_credits?: number | string | null;
  planCreditDailyLimit?: number | string | null;
  plan_credit_daily_limit?: number | string | null;
  monthly?: CreditBalancePart | null;
  currentWindow?: CreditBalancePart | null;
  current_window?: CreditBalancePart | null;
  planEntitlement?: CreditPlanEntitlement | null;
  plan_entitlement?: CreditPlanEntitlement | null;
  purchased?: CreditBalancePart | null;
  subscription?: WorkspaceSubscriptionSummary | null;
  totalAvailableDisplayMicrousd?: number;
  totalAvailableWorkUnitsDisplayMicrousd?: number;
  totalAvailableWorkUnits?: number | string | null;
}

export interface CreditLedgerEntry {
  id: string;
  source_type?: 'MONTHLY' | 'PURCHASED' | 'OVERAGE' | string;
  entry_type?: 'GRANT' | 'USAGE' | 'REFUND' | 'EXPIRE' | 'ADJUSTMENT' | 'OVERAGE' | string;
  purchased_grant_id?: string | null;
  source_order_id?: string | null;
  internal_delta_microusd?: number | string | null;
  delta_internal_microusd?: number | string | null;
  credits_delta?: number | string | null;
  work_units_delta?: number | string | null;
  workUnitsDelta?: number | string | null;
  balance_after_internal_microusd?: number | string | null;
  created_at: string;
  metadata?: Record<string, any> | null;
  total_count?: number | string | null;
}

export interface CreditLedgerResponse {
  workspace_id?: string;
  entries: CreditLedgerEntry[];
  pagination: {
    page: number;
    size: number;
    total: number;
    totalPages?: number;
    total_pages?: number;
    hasNext?: boolean;
    has_next?: boolean;
    hasPrevious?: boolean;
    has_previous?: boolean;
  };
}

export interface WorkUnitsIntegrationDetail {
  integration: string;
  read_calls: number | string;
  write_calls: number | string;
  other_calls: number | string;
  failed_calls: number | string;
}

export interface WorkUnitsUsageDetails {
  calculation_scope?: string | null;
  weighted_actions?: number | string | null;
  credit_multiplier?: number | string | null;
  external_integration_read_calls?: number | string | null;
  external_integration_write_calls?: number | string | null;
  external_integration_calls?: number | string | null;
  integration_details: WorkUnitsIntegrationDetail[];
}

export interface CreditUsageEvent {
  id: number | string;
  created_at: string;
  session_id?: string | null;
  session_name?: string | null;
  session_user_id?: string | null;
  session_user_email?: string | null;
  session_user_name?: string | null;
  capability?: string | null;
  display_cost_microusd?: number | string | null;
  work_units?: number | string | null;
  workUnits?: number | string | null;
  work_units_display_microusd?: number | string | null;
  workUnitsDisplayMicrousd?: number | string | null;
  work_units_calculation_scope?: string | null;
  workUnitsCalculationScope?: string | null;
  work_units_details?: WorkUnitsUsageDetails | null;
  workUnitsDetails?: WorkUnitsUsageDetails | null;
  total_count?: number | string | null;
}

export interface CreditUsageEventsResponse {
  workspace_id?: string;
  events: CreditUsageEvent[];
  pagination: {
    page: number;
    size: number;
    total: number;
    totalPages?: number;
    total_pages?: number;
    hasNext?: boolean;
    has_next?: boolean;
    hasPrevious?: boolean;
    has_previous?: boolean;
  };
}

export interface CreditGrant {
  id: string;
  source_order_id?: string | null;
  sourceOrderId?: string | null;
  sku_code?: string | null;
  skuCode?: string | null;
  credits_granted?: number | string | null;
  creditsGranted?: number | string | null;
  credits_used?: number | string | null;
  creditsUsed?: number | string | null;
  credits_remaining?: number | string | null;
  creditsRemaining?: number | string | null;
  work_units_granted?: number | string | null;
  workUnitsGranted?: number | string | null;
  work_units_used?: number | string | null;
  workUnitsUsed?: number | string | null;
  work_units_remaining?: number | string | null;
  workUnitsRemaining?: number | string | null;
  work_units_granted_display_microusd?: number | string | null;
  workUnitsGrantedDisplayMicrousd?: number | string | null;
  work_units_used_display_microusd?: number | string | null;
  workUnitsUsedDisplayMicrousd?: number | string | null;
  work_units_remaining_display_microusd?: number | string | null;
  workUnitsRemainingDisplayMicrousd?: number | string | null;
  internal_budget_microusd?: number | string | null;
  internalBudgetMicrousd?: number | string | null;
  internal_used_microusd?: number | string | null;
  internalUsedMicrousd?: number | string | null;
  internal_remaining_microusd?: number | string | null;
  internalRemainingMicrousd?: number | string | null;
  credit_unit_microusd?: number | string | null;
  creditUnitMicrousd?: number | string | null;
  valid_from?: string | null;
  validFrom?: string | null;
  expires_at?: string | null;
  expiresAt?: string | null;
  status?: string | null;
  metadata?: Record<string, any> | null;
  created_at?: string | null;
  createdAt?: string | null;
  updated_at?: string | null;
  updatedAt?: string | null;
}

export interface CreditGrantsResponse {
  workspace_id?: string;
  grants: CreditGrant[];
  pagination: CreditLedgerResponse['pagination'];
}

export interface BillingTransaction {
  id: string;
  date: string;
  description: string;
  type: string;
  amountCents: number;
  currency: string;
  status: string;
  creditsDelta?: number | null;
  workUnitsDelta?: number | null;
  source?: string;
  invoiceUrl?: string | null;
  pdfUrl?: string | null;
}

export interface BillingTransactionsResponse {
  pagination: GetInvoicesResponse['pagination'];
  transactions: BillingTransaction[];
}

// Plan interfaces
export interface Plan {
  id: string;
  code: string;
  productCode?: string | null;
  name: string;
  tier: string;
  pointsQuota: number;
  priceCents: number;
  /** Explicit dollar display values returned by the backend; do not derive these in the UI. */
  priceUsd?: number | null;
  chargeAmountCents?: number;
  chargeAmountUsd?: number | null;
  displayMonthlyAmountCents?: number;
  displayMonthlyAmountUsd?: number | null;
  displayBudgetMicrousdMonthly?: number;
  workUnitsBudgetMonthly?: number;
  workUnitsBudgetDisplayMicrousdMonthly?: number;
  estimatedMonthlyOperations?: {
    min: number;
    max: number;
  };
  currency: string;
  billingCycle: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  rules?: Record<string, unknown> | null;
}

export interface PluginRequestQuotaAccount {
  id: string;
  productCode?: string;
  workspaceId: string;
  subscriptionId: string;
  planId: string;
  periodStart: string;
  periodEnd: string;
  quotaUnits: number;
  reservedUnits: number;
  consumedUnits: number;
  remainingUnits: number;
  status: string;
}

export interface PluginRequestUsageEvent {
  id: string;
  productCode?: string;
  quotaAccountId: string;
  workspaceId: string;
  subscriptionId: string;
  userId?: string | null;
  taskId?: string | null;
  requestId: string;
  toolName: string;
  units: number;
  eventType: string;
  status: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export interface PlanComparisonLimitValue {
  value?: number | null;
  unlimited?: boolean | null;
  configured?: boolean | null;
}

export interface PlanComparisonCatalogPlan {
  planCode?: string | null;
  displayName?: string | null;
  planFamily?: string | null;
  variant?: string | null;
  rank?: number | null;
  prices?: {
    monthlyAmountCents?: number | null;
    monthlyAmountUsd?: number | null;
    yearlyAmountCents?: number | null;
    yearlyAmountUsd?: number | null;
    yearlyDisplayMonthlyAmountCents?: number | null;
    yearlyDisplayMonthlyAmountUsd?: number | null;
  } | null;
  limits?: {
    workspaceUsers?: PlanComparisonLimitValue | null;
    storeConnections?: PlanComparisonLimitValue | null;
    brandGroups?: PlanComparisonLimitValue | null;
    supportsSlackAndFeishu?: boolean | null;
    otherIntegrationConnections?: PlanComparisonLimitValue | null;
    totalIntegrationConnections?: PlanComparisonLimitValue | null;
  } | null;
  monthlyWorkUnits?: number | null;
  estimatedMonthlyOperations?: {
    min?: number | null;
    max?: number | null;
  } | null;
}

export interface PlanComparisonCatalogResponse {
  plans?: PlanComparisonCatalogPlan[] | null;
}

export interface SubscriptionSummaryCardState {
  planCode?: string;
  billingCycle?: 'monthly' | 'yearly';
  buttonText?: string;
  disabled?: boolean;
  action?: 'upgrade' | 'renew' | 'manage' | 'switch_cycle' | 'current_plan' | string;
}

export interface WorkspaceSubscriptionSummary {
  title?: string;
  badge?: string;
  supportingText?: string;
  provider?: string | null;
  billingProvider?: string | null;
  paymentProvider?: string | null;
  paymentStatus?: string | null;
  payment_status?: string | null;
  shopifyPaymentStatus?: string | null;
  shopify_payment_status?: string | null;
  paymentPending?: boolean | null;
  payment_pending?: boolean | null;
  isPaymentPending?: boolean | null;
  is_payment_pending?: boolean | null;
  shopifyPaymentPending?: boolean | null;
  shopify_payment_pending?: boolean | null;
  reasonCode?: string | null;
  reason_code?: string | null;
  planCreditRefreshCadence?: string | null;
  plan_credit_refresh_cadence?: string | null;
  planCode?: string;
  planName?: string;
  billingCycle?: 'monthly' | 'yearly';
  status?: string;
  isTrial?: boolean;
  isAutoRenew?: boolean;
  cancelAtPeriodEnd?: boolean | null;
  cancel_at_period_end?: boolean | null;
  nextBillingAt?: string | null;
  expiresAt?: string | null;
  actionLabel?: string;
  primaryAction?: string;
  primaryActionMode?: string;
  statusLabel?: string;
  statusMessage?: string;
  displayBudgetMicrousd?: number | null;
  displayUsedMicrousd?: number | null;
  displayRemainingMicrousd?: number | null;
  usageStatus?: string | null;
  bannerType?: string | null;
  bannerTitle?: string | null;
  bannerDescription?: string | null;
  bannerAction?: string | null;
  cards?: SubscriptionSummaryCardState[] | Record<string, SubscriptionSummaryCardState>;
}

export interface CancelSubscriptionResponse {
  success?: boolean;
  status?: string | null;
  workspacePlan?: Record<string, any> | null;
  workspace_plan?: Record<string, any> | null;
  subscription?: WorkspaceSubscriptionSummary | null;
  message?: string | null;
}

export interface SetupCardStatusResponse {
  hasSetupCard: boolean;
  hasActiveSubscription: boolean;
  trialBudgetExhausted?: boolean;
  displayBudgetMicrousd?: number;
  displayUsedMicrousd?: number;
  canActivatePaidNow?: boolean;
  scheduledChange?: {
    changeType?: string | null;
    effectiveMode?: string | null;
    effectiveAt?: string | null;
    fromPlanCode?: string | null;
    fromPlanName?: string | null;
    fromBillingCycle?: string | null;
    toPlanCode?: string | null;
    toPlanName?: string | null;
    toBillingCycle?: string | null;
  } | null;
  subscription?: {
    id?: string;
    workspaceId?: string;
    planId?: string;
    planCode?: string | null;
    planName?: string | null;
    billingCycle?: string | null;
    provider?: string;
    status?: string;
    isInTrial?: boolean;
    trialEndAt?: string | null;
    currentPeriodEnd?: string | null;
    cancelAtPeriodEnd?: boolean | null;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    displayBudgetMicrousd?: number;
    displayUsedMicrousd?: number;
    trialBudgetExhausted?: boolean;
    canActivatePaidNow?: boolean;
  } | null;
}

export interface ActivateTrialNowResponse {
  success: boolean;
  workspaceId?: string;
  subscriptionId?: string;
  stripeSubscriptionId?: string;
  message?: string;
  activation?: {
    success?: boolean;
    alreadyActive?: boolean;
    status?: string;
    trialEnd?: number | null;
  };
}

export interface ActivateOnboardingTrialResponse {
  workspaceId?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  workspaceStatus?: string;
  redirectPath?: string;
  activationMode?: 'trial_activated'
    | 'checkout_required'
    | 'trial_checkout_required'
    | 'setup_card_required'
    | 'direct_card_required'
    | 'invalid_setup_session'
    | 'invalid_request'
    | 'invalid_plan'
    | 'checkout_failed'
    | 'plugin_free_activated'
    | 'existing_subscription'
    | 'not_applicable';
  paymentUrl?: string;
  message?: string;
  planId?: string;
  planCode?: string;
  planName?: string;
  billingCycle?: 'monthly' | 'yearly' | string;
}

export interface PluginTrialCheckoutRequest {
  workspaceId: string;
  planId: string;
  successUrl?: string;
  cancelUrl?: string;
  axiosRequestConfig?: any;
}

export class BillingService {
  private api: Api;

  constructor(config: ApiConfig) {
    this.api = new Api(config);
  }

  // Get user plan information
  async getUserPlans(): Promise<UserPlan> {
    const response = await this.api.get<UserPlan>('/api/user/plans');
    return { ...response };
  }

  // Get user points information
  async getUserPoints(): Promise<UserPoints> {
    return this.api.get('/api/user/points');
  }

  // Get user points history
  async getUserPointsHistory(params: GetUserPointsHistoryParams): Promise<GetUserPointsHistoryResponse> {
    return this.api.get('/api/user/points/history', { params });
  }

  // 首次、过期、切换 创建订阅
  async createSubscription({ planId, successUrl, cancelUrl, allowTrial, axiosRequestConfig }: { planId: string; successUrl: string; cancelUrl: string; allowTrial?: boolean; axiosRequestConfig?: any }): Promise<any> {
    const data: CreateSubscriptionRequest = {
      planId,
      successUrl,
      cancelUrl,
    };
    if (typeof allowTrial === 'boolean') {
      data.allowTrial = allowTrial;
    }
    return this.api.post('/api/stripe/subscription/create', data, {
      ...axiosRequestConfig,
      params: data,
    });
  }

  async createPluginSubscription({ planId, successUrl, cancelUrl, axiosRequestConfig }: { planId: string; successUrl: string; cancelUrl: string; axiosRequestConfig?: any }): Promise<any> {
    const data: CreateSubscriptionRequest = {
      planId,
      successUrl,
      cancelUrl,
    };
    return this.api.post('/api/products/plugin/billing/subscription/create', data, {
      ...withPluginProductHeaders(axiosRequestConfig),
      params: data,
    });
  }

  // 续订
  async renewSubscription({ planId, successUrl, cancelUrl, axiosRequestConfig }: { planId: string; successUrl: string; cancelUrl: string; axiosRequestConfig?: any }): Promise<any> {
    const data: CreateSubscriptionRequest = {
      planId,
      successUrl,
      cancelUrl,
    };
    return this.api.post('/api/stripe/subscription/renew', data, {
      ...axiosRequestConfig,
      params: data,
    });
  }

  async renewPluginSubscription({ targetPlanId, axiosRequestConfig }: { targetPlanId?: string; axiosRequestConfig?: any } = {}): Promise<any> {
    const data = targetPlanId ? { targetPlanId } : {};
    return this.api.post(
      '/api/products/plugin/billing/subscription/renew',
      data,
      withPluginProductHeaders(axiosRequestConfig),
    );
  }

  // 跳转 stripe
  async createSubscriptionPortal(returnUrl: string): Promise<CreateSubscriptionPortalResponse> {
    return this.api.post('/api/stripe/subscription/portal?returnUrl=' + returnUrl);
  }

  async createPluginSubscriptionPortal(returnUrl: string, axiosRequestConfig?: any): Promise<CreateSubscriptionPortalResponse> {
    return this.api.post(
      '/api/products/plugin/billing/portal?returnUrl=' + returnUrl,
      {},
      withPluginProductHeaders(axiosRequestConfig),
    );
  }

  async cancelSubscription(axiosRequestConfig?: any): Promise<CancelSubscriptionResponse> {
    return this.api.post('/api/billing/subscription/cancel', {}, axiosRequestConfig);
  }

  // Get invoices
  async getInvoices(params?: { page?: number; size?: number }): Promise<GetInvoicesResponse> {
    return this.api.get('/api/stripe/invoices', withProductHeaders(getCurrentProductCode(), { params }));
  }

  async getCreditTopupSkus(): Promise<CreditTopupSku[]> {
    return this.api.get('/api/billing/credit-topup/skus', {
      headers: {
        [SKIP_AUTH_HEADER]: 'true',
        [SKIP_WORKSPACE_ID_HEADER]: 'true',
      },
    });
  }

  async getCreditTopupEligibility(): Promise<CreditTopupEligibilityResponse> {
    return this.api.get('/api/billing/credit-topup/eligibility');
  }

  async createCreditTopupCheckout(request: CreateCreditTopupCheckoutRequest): Promise<CreateCreditTopupCheckoutResponse> {
    return this.api.post('/api/billing/credit-topup/checkout', request);
  }

  async syncCreditTopupCheckoutSession(params: SyncCreditTopupCheckoutSessionRequest): Promise<SyncCreditTopupCheckoutSessionResponse> {
    return this.api.get('/api/billing/credit-topup/checkout/sync', { params });
  }

  async getCreditSummary(params?: { refresh?: boolean }): Promise<CreditSummaryResponse> {
    if (params?.refresh) {
      return this.api.get('/api/billing/credits/summary', {
        params: { refresh: true },
      });
    }
    return this.api.get('/api/billing/credits/summary');
  }

  async getCreditLedger(params?: { page?: number; size?: number }): Promise<CreditLedgerResponse> {
    return this.api.get('/api/billing/credits/ledger', { params });
  }

  async getCreditUsageEvents(params?: { page?: number; size?: number; refresh?: boolean }): Promise<CreditUsageEventsResponse> {
    return this.api.get('/api/billing/credits/usage-events', { params });
  }

  async getCreditGrants(params?: { page?: number; size?: number; refresh?: boolean }): Promise<CreditGrantsResponse> {
    return this.api.get('/api/billing/credits/grants', { params });
  }

  async getBillingTransactions(params?: { page?: number; size?: number }): Promise<BillingTransactionsResponse> {
    return this.api.get('/api/billing/credits/transactions', withProductHeaders(getCurrentProductCode(), { params }));
  }

  // Get all available plans
  async getPlans(axiosRequestConfig?: any): Promise<Plan[]> {
    return this.api.get('/api/plans', axiosRequestConfig);
  }

  async getPluginPlans(axiosRequestConfig?: any): Promise<Plan[]> {
    return this.api.get('/api/products/plugin/billing/plans', withPluginProductHeaders(axiosRequestConfig));
  }

  async getPluginPlan(axiosRequestConfig?: any): Promise<Plan | null> {
    return this.api.get('/api/products/plugin/billing/plan', withPluginProductHeaders(axiosRequestConfig));
  }

  async getPluginRequestQuota(workspaceId: string, axiosRequestConfig?: any): Promise<PluginRequestQuotaAccount> {
    return this.api.get('/api/products/plugin/billing/usage', withPluginProductHeaders({
      ...(axiosRequestConfig || {}),
      headers: {
        ...(axiosRequestConfig?.headers || {}),
        'X-Workspace-Id': workspaceId,
      },
    }));
  }

  async getPluginRequestUsageEvents(workspaceId: string, params?: { limit?: number }, axiosRequestConfig?: any): Promise<PluginRequestUsageEvent[]> {
    return this.api.get('/api/products/plugin/billing/usage/events', withPluginProductHeaders({
      ...(axiosRequestConfig || {}),
      params,
      headers: {
        ...(axiosRequestConfig?.headers || {}),
        'X-Workspace-Id': workspaceId,
      },
    }));
  }

  async getPlanComparisonCatalog(axiosRequestConfig?: any): Promise<PlanComparisonCatalogResponse> {
    return this.api.get('/api/plans/comparison', axiosRequestConfig);
  }

  async getPublicPlanComparisonCatalog(): Promise<PlanComparisonCatalogResponse> {
    return this.api.get('/api/public/plans/comparison', {
      headers: {
        [SKIP_AUTH_HEADER]: 'true',
        [SKIP_WORKSPACE_ID_HEADER]: 'true',
      },
    });
  }

  async getSetupCardStatus(axiosRequestConfig?: any): Promise<SetupCardStatusResponse> {
    return this.api.get('/api/stripe/setup-card/status', axiosRequestConfig);
  }

  async getPluginSetupCardStatus(axiosRequestConfig?: any): Promise<SetupCardStatusResponse> {
    return this.api.get('/api/products/plugin/billing/setup-status', withPluginProductHeaders(axiosRequestConfig));
  }

  async activateTrialSubscriptionNow(axiosRequestConfig?: any): Promise<ActivateTrialNowResponse> {
    return this.api.post('/api/stripe/subscription/trial/activate-now', {}, axiosRequestConfig);
  }

  async activateOnboardingTrial({ planId, successUrl, cancelUrl, axiosRequestConfig }: { planId: string; successUrl?: string; cancelUrl?: string; axiosRequestConfig?: any }): Promise<ActivateOnboardingTrialResponse> {
    return this.api.post('/api/workspaces/subscription/onboarding-trial', { planId, successUrl, cancelUrl }, axiosRequestConfig);
  }

  async activatePluginTrialCheckout({
    workspaceId,
    planId,
    successUrl,
    cancelUrl,
    axiosRequestConfig,
  }: PluginTrialCheckoutRequest): Promise<ActivateOnboardingTrialResponse> {
    return this.api.post(
      `/api/products/plugin/workspaces/${workspaceId}/trial/checkout`,
      { planId, successUrl, cancelUrl },
      withPluginProductHeaders({
        ...(axiosRequestConfig || {}),
        headers: {
          ...(axiosRequestConfig?.headers || {}),
          'X-Workspace-Id': workspaceId,
        },
      }),
    );
  }

  async activatePluginFreePlan({ workspaceId, axiosRequestConfig }: { workspaceId: string; axiosRequestConfig?: any }): Promise<ActivateOnboardingTrialResponse> {
    return this.api.post(
      `/api/products/plugin/workspaces/${workspaceId}/free-plan/activate`,
      {},
      withPluginProductHeaders({
        ...(axiosRequestConfig || {}),
        headers: {
          ...(axiosRequestConfig?.headers || {}),
          'X-Workspace-Id': workspaceId,
        },
      }),
    );
  }
}

export const billingService = new BillingService({
  baseURL: config.services.billing.apiHost,
  headers: { 'Content-Type': 'application/json' },
});
